import { useEffect, useRef, useState } from 'preact/hooks';
import { createAtlasChannel, type AtlasChannel } from '../lib/atlasChannel';
import { createBuilderFeedChannel } from '../lib/builderFeedChannel';

/** H4a (COCKPIT-HOME-COLUMNS-SPEC.md §H4) — the ONE builder-feed channel
 * instance for the current (tenant, project) pair, same ref-held,
 * recreate-on-change shape `useAtlasChannel` already uses (that file's own
 * header explains why: held in a ref rather than React/Preact state, so
 * CockpitFrame's render never races its own disposal).
 *
 * `project === null` (every screen except a project one on a builder slug)
 * returns the honest disconnected default — no poll, nothing to show —
 * exactly like `useAtlasChannel(null)` does for the Atlas channel. The
 * Atlas channel itself is UNTOUCHED by this hook: CockpitFrame keeps
 * calling `useAtlasChannel(tenantId)` unconditionally, so its draft/
 * conversation survive a trip through a builder occupant and back (H2's
 * own rule) — this hook only ever backs the SEPARATE builder occupant. */
export function useBuilderChannel(tenantId: string | null, project: string | null): AtlasChannel {
  const ref = useRef<{ key: string | null; channel: AtlasChannel } | null>(null);
  const key = tenantId && project ? `${tenantId}:${project}` : null;
  if (!ref.current || ref.current.key !== key) {
    ref.current?.channel.dispose?.();
    ref.current = {
      key,
      channel: tenantId && project ? createBuilderFeedChannel({ tenantId, project }) : createAtlasChannel(),
    };
  }
  const channel = ref.current.channel;

  const [, bump] = useState(0);
  useEffect(() => channel.subscribe(() => bump((n) => n + 1)), [channel]);
  useEffect(() => () => ref.current?.channel.dispose?.(), []);

  return channel;
}
